import Paper from "@mui/material/Paper/Paper";
import React, {useEffect} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {Box, Grid, IconButton, Typography} from "@mui/material";
import remarkBreaks from "remark-breaks/index";
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {coy} from 'react-syntax-highlighter/dist/esm/styles/prism'
import rehypeRaw from 'rehype-raw';
import EmptyStateIcon from "../icons/EmptyStateIcon";
import ThumbsUp from "../icons/ThumbsUp";
import ThumbsDown from "../icons/ThumbsDown";
import {GradientButton, GradientText} from "./GradientComponents";
import MediumSquare from "../icons/MediumSquare";
import "./components.css"

interface OutputBoxProps {
    isGenerating: boolean;
    stopGenerating: () => void;
    outputText: string;
    ratingSubmitted: (rating: number) => void;
}

const OutputBox = ({isGenerating, outputText, stopGenerating, ratingSubmitted}: OutputBoxProps) => {
    const [rating, setRating] = React.useState<number | null>(null);
    const [startTime, setStartTime] = React.useState<number | null>(null);
    const [endTime, setEndTime] = React.useState<number | null>(null);
    const [startedGenerating, setStartedGenerating] = React.useState<boolean>(false);

    useEffect(() => {
        if (isGenerating) {
            setRating(null);
        } else {
            setStartedGenerating(false);
            setEndTime(Date.now());
        }

    }, [isGenerating]);

    useEffect(() => {
        if (isGenerating && outputText && outputText.length > 0 && !startedGenerating) {
            setStartedGenerating(true)
            setStartTime(Date.now());
        }
    }, [outputText, startedGenerating, isGenerating]);

    // isGenerating = true;
    // outputText = "Here's a sample JSON: \n\n ```json \n{\"name\": \"John Doe\", \"age\": 30, \"car\": null} \n ```" +
    //     "\n\n Here's a sample Python code: \n\n ```python\n import numpy as np \n ```" +
    //     "\n\n Here a sample table: \n\n | Column 1 | Column 2 | Column 3 |\n | -------- | -------- | -------- |\n | 1 | 2 | 3 |\n | 4 | 5 | 6 |\n" +
    //     "\n\n Here's a sample mermaid diagram: \n\n ```mermaid\n graph LR\n A[Square Rect] -- Link text --> B((Circle))\n A --> C(Round Rect)\n B --> D{Rhombus}\n C --> D\n ```" +
    //     "\n\n Here's code without language: \n\n ```\n import numpy as np \n ```"

    return (
        <Box>
            <Typography variant={"body2"} sx={{mb: 2}} style={{fontWeight: 600}}>Output</Typography>
            <Box sx={{display: 'flex', p: 1}} style={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
            }}>
                {(isGenerating || outputText) ? (<Paper variant={"outlined"} style={{
                    // visibility: (isGenerating || outputText) ? "visible" : "hidden",
                    borderRadius: "8px",
                    width: '100%',
                    border: "1px solid #E2E8F0",
                    boxShadow: '0px 1px 2px 0px #0F1A2A14',
                    minHeight: '120px'
                }}>
                    <Box sx={{display: 'flex', p: 1}}>
                        {outputText ? (
                            <Box sx={{p: 1, width: '100%'}}>
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm, remarkBreaks]}
                                    rehypePlugins={[rehypeRaw]}
                                    skipHtml={true}
                                    components={{
                                        code({node, inline, className, children, ...props}) {
                                            const match = /language-(\w+)/.exec(className || '')
                                            const text = String(children).replace(/<span class="typing-cursor" \/>/, '');
                                            return !inline && match ? (
                                                <SyntaxHighlighter
                                                    {...props}
                                                    wrapLongLines={true}
                                                    children={text}
                                                    style={coy}
                                                    language={match[1]}
                                                    PreTag="div"
                                                />
                                            ) : (
                                                <SyntaxHighlighter
                                                    {...props}
                                                    wrapLongLines={true}
                                                    children={text}
                                                    style={coy}
                                                    PreTag="div"
                                                />
                                                // <code {...props} className={className}>
                                                //     {children}
                                                // </code>
                                            )
                                        },
                                        span: ({node, className, children, ...props}) => {
                                            const match = /typing-cursor/.exec(className || '')
                                            if (match) {
                                                return (
                                                    <span {...props} className="typing-cursor"/>
                                                );
                                            }
                                            return (
                                                <span {...props} className={className}
                                                      children={String(children)}></span>
                                            );
                                        }
                                    }}
                                >
                                    {
                                        isGenerating ? (`${outputText} <span class="typing-cursor" />`) : outputText
                                    }
                                    {/*{`${outputText}`}*/}

                                </ReactMarkdown>
                                {/*<MuiMarkdown*/}
                                {/*    Highlight={Highlight}*/}
                                {/*    themes={themes}*/}
                                {/*    prismTheme={themes.duotoneLight}*/}
                                {/*    codeWrapperStyles={{*/}
                                {/*        borderRadius: 8,*/}
                                {/*        overflow: 'auto',*/}
                                {/*    }}*/}

                                {/*    hideLineNumbers*/}
                                {/*>{outputText}</MuiMarkdown>*/}
                                {
                                    (isGenerating || outputText) && (
                                        <Grid container style={{
                                            paddingTop: 24
                                        }}>

                                            <Grid item xs={6} style={{
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                display: 'flex',
                                            }}>
                                                {
                                                    isGenerating && (
                                                        <GradientButton variant={"text"}
                                                                        startIcon={<MediumSquare gradient/>}
                                                                        style={{
                                                                            textTransform: 'none',
                                                                        }}
                                                                        onClick={() => stopGenerating()}
                                                        >
                                                            <GradientText
                                                                variant={"body2"}>
                                                                Stop Generating
                                                            </GradientText>
                                                        </GradientButton>
                                                    )
                                                }

                                                {
                                                    !isGenerating && startTime && endTime && (
                                                        <Grid item xs={6} style={{
                                                            justifyContent: 'flex-start',
                                                            display: 'flex',
                                                            alignItems: 'left'
                                                        }}>
                                                            <Typography variant={"body2"} style={{
                                                                color: '#617187'
                                                            }}>Generated in {((endTime - startTime) / 1000).toFixed(2)}s</Typography>
                                                        </Grid>
                                                    )
                                                }

                                            </Grid>

                                            {
                                                !isGenerating && (
                                                    <Grid item xs={6} style={{
                                                        justifyContent: 'flex-end',
                                                        alignItems: 'center',
                                                        display: 'flex',
                                                    }}>

                                                        <Grid item xs={2} style={{
                                                            justifyContent: 'flex-end',
                                                            alignItems: 'center',
                                                            display: 'flex',
                                                        }} sx={{mr: 1}}>
                                                            <Typography variant={"body2"} style={{
                                                                color: '#617187'
                                                            }}>Feedback</Typography>
                                                        </Grid>
                                                        <Grid item xs={0.5} style={{
                                                            justifyContent: 'right',
                                                            alignItems: 'right',
                                                            display: 'flex',
                                                            minWidth: 30
                                                        }}>
                                                            <IconButton size={"small"}
                                                                        disabled={rating === 0 || rating === 1}
                                                                        onClick={() => {
                                                                            setRating(1);
                                                                            ratingSubmitted(1);
                                                                        }}>
                                                                <ThumbsUp variant={rating === 1 ? 'filled' : 'outlined'}/>
                                                            </IconButton>
                                                        </Grid>
                                                        <Grid item xs={0.5} style={{
                                                            justifyContent: 'right',
                                                            alignItems: 'right',
                                                            display: 'flex',
                                                            minWidth: 30
                                                        }}>
                                                            <IconButton size={"small"}
                                                                        disabled={rating === 0 || rating === 1}
                                                                        onClick={() => {
                                                                            setRating(0);
                                                                            ratingSubmitted(0);
                                                                        }}>
                                                                <ThumbsDown variant={rating === 0 ? 'filled' : 'outlined'}/>
                                                            </IconButton>
                                                        </Grid>
                                                    </Grid>
                                                )
                                            }
                                        </Grid>)
                                }
                            </Box>
                        ) : (<Box style={{
                            display: 'flex',
                            justifyContent: 'center',
                            position: 'relative',
                            paddingLeft: 24,
                            paddingTop: 12
                        }}>
                            <div className={"dot-typing"}/>
                        </Box>)
                        }
                    </Box>
                </Paper>) : (
                    <EmptyStateIcon/>
                )
                }
            </Box>
        </Box>
    )
};

export default OutputBox;
